import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Box, Group, Text, TextInput, ActionIcon } from "@mantine/core";
import {
  IconDownload,
  IconUpload,
  IconPlus,
  IconEdit,
  IconTrash,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { TableActions, TableAction, actionsColumn } from "../TableActions";
import { useSetActionButtons } from "../../services/ActionButtonsContext";
import { openExportModal } from "../Translations/ExportModal";
import { openImportModal } from "../Translations/ImportModal";
import { DataTable } from "../DataTable";
import {
  useGetAllTranslations,
  useGetTranslationKeyMeta,
  useGetTranslationsByKey,
} from "../../api/translations";
import { useSearchParams } from "react-router-dom";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import TranslationModal from "../Translations/TranslationModal";
import DeleteTranslationModal from "../Translations/DeleteTranslationModal";
import VersionInfoCard from "../Translations/VersionInfoCard";
import CoverageCard from "../Translations/CoverageCard";
import KeyMetaDrawer from "../Translations/KeyMetaDrawer";
import { describeLocation } from "../Translations/describeLocation";
import { useGetAllActiveLanguages } from "../../api/languages";

// Stable while the notes load, so the columns aren't rebuilt every render.
const NO_KEY_META = {};

function AiColumnTitle({ label }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="sm" fw={600}>
        {label}
      </Text>
      <Badge size="xs" variant="outline" color="gray">
        AI
      </Badge>
    </Group>
  );
}

function TranslationsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editingKey, setEditingKey] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [metaKey, setMetaKey] = useState(null);
  const [createKey, setCreateKey] = useState(null);
  const [coverageDeleteKey, setCoverageDeleteKey] = useState(null);

  const { data: coverageDeleteData } =
    useGetTranslationsByKey(coverageDeleteKey);
  const { data: keyMetaData } = useGetTranslationKeyMeta();
  const keyMeta = keyMetaData?.keys ?? NO_KEY_META;

  const page = parseInt(searchParams.get("page") || 1);
  const limit = parseInt(searchParams.get("limit") || 25);

  const sorting = useMemo(() => {
    try {
      const sortParams = searchParams.get("sort");
      return sortParams
        ? JSON.parse(sortParams)
        : { columnAccessor: "translationKey", direction: "asc" };
    } catch (e) {
      return { columnAccessor: "translationKey", direction: "asc" };
    }
  }, [searchParams]);

  const filter = useMemo(() => {
    try {
      const filterParams = searchParams.get("filter");
      return filterParams ? JSON.parse(filterParams) : null;
    } catch (e) {
      return null;
    }
  }, [searchParams]);

  const [debouncedFilter] = useDebouncedValue(filter, 300);

  const { data, isPending } = useGetAllTranslations({
    page,
    limit,
    sort: JSON.stringify(sorting),
    filter: debouncedFilter,
  });

  const { data: languages } = useGetAllActiveLanguages();

  const LANGUAGES = languages?.data?.map((lang) => lang.code) || [];

  const actions = useMemo(
    () => [
      {
        label: "Add Translation",
        icon: <IconPlus />,
        onClick: () => {
          setEditingKey(null);
          openModal();
        },
      },
      {
        label: "Import",
        icon: <IconUpload />,
        onClick: openImportModal,
      },
      {
        label: "Export",
        icon: <IconDownload />,
        onClick: openExportModal,
      },
    ],
    [openModal]
  );

  useSetActionButtons(actions);

  const handlePageChange = (newPage) => {
    setSearchParams((params) => {
      params.set("page", newPage.toString());
      return params;
    });
  };

  const handleSortChange = (sort) => {
    setSearchParams((params) => {
      if (!sort) {
        params.delete("sort");
      } else {
        params.set("sort", JSON.stringify(sort));
      }
      return params;
    });
  };

  const handleFilterChange = (newFilter) => {
    setSearchParams((params) => {
      params.set("page", "1");
      if (!newFilter) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(newFilter));
      }
      return params;
    });
  };

  // Stable, so the memoized CoverageCard doesn't re-render with the tab.
  const handleEdit = useCallback(
    (translationKey) => {
      setEditingKey(translationKey);
      openModal();
    },
    [openModal]
  );

  const handleCreateMissing = useCallback(
    (translationKey) => {
      setEditingKey(null);
      setCreateKey(translationKey);
      openModal();
    },
    [openModal]
  );

  const handleCloseModal = () => {
    setEditingKey(null);
    setCreateKey(null);
    closeModal();
  };

  // Deleting from the coverage card only gives us the key string, so
  // fetch its per-language rows first, then open the delete modal.
  useEffect(() => {
    if (!coverageDeleteKey || !coverageDeleteData) return;

    const rows = Array.isArray(coverageDeleteData)
      ? coverageDeleteData
      : coverageDeleteData.data || [];

    setRecordToDelete({
      translationKey: coverageDeleteKey,
      ids: Object.fromEntries(rows.map((row) => [row.languageCode, row.id])),
      languages: Object.fromEntries(
        rows.map((row) => [row.languageCode, row.value])
      ),
    });
    openDeleteModal();
    setCoverageDeleteKey(null);
  }, [coverageDeleteKey, coverageDeleteData, openDeleteModal]);

  const handleDelete = (record) => {
    setRecordToDelete(record);
    openDeleteModal();
  };

  const handleCloseDeleteModal = () => {
    setRecordToDelete(null);
    closeDeleteModal();
  };

  const sortedData = useMemo(() => {
    if (!data?.data) return [];

    const records = [...data.data];

    if (sorting?.columnAccessor?.startsWith("languages.")) {
      const langCode = sorting.columnAccessor.replace("languages.", "");

      records.sort((a, b) => {
        const valA = a.languages[langCode] || "";
        const valB = b.languages[langCode] || "";
        const comparison = valA.localeCompare(valB);
        return sorting.direction === "asc" ? comparison : -comparison;
      });
    }

    return records;
  }, [data?.data, sorting]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessor: "translationKey",
        title: "Translation Key",
        sortable: true,
        width: 240,
        render: ({ translationKey }) => (
          <Text size="sm" fw={500}>
            {translationKey}
          </Text>
        ),
        filter: (
          <TextInput
            label="Translation Key"
            description="Filter by translation key"
            placeholder="Search..."
            leftSection={<IconSearch size={16} />}
            rightSection={
              filter?.translationKey && (
                <ActionIcon
                  size="sm"
                  variant="transparent"
                  c="dimmed"
                  onClick={() => handleFilterChange(null)}
                >
                  <IconX size={14} />
                </ActionIcon>
              )
            }
            value={filter?.translationKey || ""}
            onChange={(e) => {
              const value = e.currentTarget.value;
              handleFilterChange(value ? { translationKey: value } : null);
            }}
          />
        ),
        filtering: filter?.translationKey,
      },
      {
        accessor: "meta.description",
        title: <AiColumnTitle label="Description" />,
        width: 340,
        render: ({ translationKey }) => {
          const meta = keyMeta[translationKey];
          return meta ? (
            <Text size="sm" c="dimmed">
              {meta.description}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              No notes yet
            </Text>
          );
        },
      },
      {
        accessor: "meta.locations",
        title: <AiColumnTitle label="Where used" />,
        width: 230,
        render: ({ translationKey }) => {
          const meta = keyMeta[translationKey];
          if (!meta) return null;
          return (
            <Box>
              {meta.locations.slice(0, 2).map((loc) => {
                const { app, color, label } = describeLocation(loc);
                return (
                  <Group key={loc.file} gap={6} wrap="nowrap">
                    <Badge
                      size="xs"
                      variant="outline"
                      color={color}
                      style={{ flexShrink: 0 }}
                    >
                      {app}
                    </Badge>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {label}
                    </Text>
                  </Group>
                );
              })}
              {meta.locations.length > 2 && (
                <Text size="xs" c="dimmed">
                  +{meta.locations.length - 2} more
                </Text>
              )}
            </Box>
          );
        },
      },
    ];

    const languageColumns = LANGUAGES?.map((langCode) => ({
      accessor: `languages.${langCode}`,
      title: langCode.toUpperCase(),
      sortable: true,
      width: 220,
      render: (record) => {
        const value = record.languages[langCode];
        if (value) {
          return (
            <Text size="sm" lineClamp={2}>
              {value}
            </Text>
          );
        }
        const hasDraft =
          keyMeta[record.translationKey]?.suggestions?.[langCode];
        return hasDraft ? (
          <Badge size="sm" variant="light" color="yellow">
            Draft ready
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">
            -
          </Text>
        );
      },
    }));

    const actionColumn = actionsColumn(
      (record) => (
        <TableActions>
          <TableAction
            label="Edit translation"
            icon={IconEdit}
            color="blue"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record.translationKey);
            }}
          />
          <TableAction
            label="Delete translation key"
            icon={IconTrash}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record);
            }}
          />
        </TableActions>
      ),
      { title: "Actions" }
    );

    return [...baseColumns, ...languageColumns, actionColumn];
  }, [filter, LANGUAGES, keyMeta]);

  return (
    <Box>
      <VersionInfoCard />
      <CoverageCard
        onCreateKey={handleCreateMissing}
        onEditKey={handleEdit}
        onDeleteKey={setCoverageDeleteKey}
      />

      <DataTable
        records={sortedData}
        idAccessor="translationKey"
        pagination={{ page, limit }}
        onPageChange={handlePageChange}
        page={page}
        recordsPerPage={limit}
        totalRecords={data?.pagination?.total}
        onSortStatusChange={handleSortChange}
        sortStatus={sorting}
        borderRadius="md"
        verticalSpacing="md"
        columns={columns}
        fetching={isPending}
        pinLastColumn
        onRowClick={({ record }) => setMetaKey(record.translationKey)}
      />

      <KeyMetaDrawer
        opened={!!metaKey}
        onClose={() => setMetaKey(null)}
        record={sortedData.find((r) => r.translationKey === metaKey)}
        meta={keyMeta[metaKey]}
        validLanguages={LANGUAGES}
      />

      <TranslationModal
        opened={modalOpened}
        onClose={handleCloseModal}
        translationKey={editingKey}
        mode={editingKey ? "edit" : "create"}
        validLanguages={LANGUAGES}
        initialKey={createKey}
      />

      <DeleteTranslationModal
        opened={deleteModalOpened}
        onClose={handleCloseDeleteModal}
        record={recordToDelete}
        validLanguages={LANGUAGES}
      />
    </Box>
  );
}

export default TranslationsTab;
